import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Pencil, ShieldCheck, Trash2 } from 'lucide-react';
import type { DeviceDetail, PortForward } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { Button, Card, Input, Pill, Spinner } from '../../components/ui';
import { useUnsavedDraft } from '../../components/ui/CollapsibleGroup';

type Notify = (message: string, tone?: 'info' | 'danger') => void;

interface FirewallResult {
  executed: boolean;
  warnings: string[];
}

/**
 * Security for one ONU — the firewall, and subscriber port-forwards.
 *
 * Both are ONU-config first (the prefer-ONU-config rule): the firewall goes over
 * TR-069 when the CPE exposes the control (either vendor), and falls back to the
 * OLT's OMCI verb on a ZTE that is not on TR-069; the single `/firewall` route
 * decides which, so this card just asks for on or off. Port-forwards are a pure
 * TR-069 feature — a subscriber's `PortMapping` table — so they show only when
 * the ONU is on TR-069 and the model carries the table.
 *
 * The card refuses to pretend: where neither firewall path exists (an OMCI-only
 * Huawei), it says the firewall is not controllable from here rather than
 * offering a switch that would silently do nothing.
 */
export function SecurityCard({
  oltId,
  onuId,
  vendor,
  deviceId,
  device,
  onNotice,
}: {
  oltId: string;
  onuId: string;
  vendor: string;
  deviceId?: string | null;
  device: DeviceDetail | null;
  onNotice: Notify;
}) {
  const { can } = useSession();
  const queryClient = useQueryClient();

  // The recorded OLT-side firewall, for an OMCI ONU the CPE cannot report.
  const omci = useQuery({
    queryKey: ['onu', onuId, 'omci-config'],
    queryFn: () =>
      api.get<{ firewalled?: boolean | null }>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/omci-config`,
      ),
  });

  // Prefer what the CPE itself reports over TR-069; fall back to the recorded
  // OLT intent. Null on both = never set, so the pill reads neither on nor off.
  const firewalled = device?.firewall ?? omci.data?.firewalled ?? null;
  // Controllable when the CPE exposes a firewall param (either vendor over
  // TR-069) OR it is a ZTE whose OLT carries the OMCI verb.
  const firewallSupported = Boolean(device?.firewallSupported) || vendor === 'zte';

  const setFirewall = useMutation({
    mutationFn: (enabled: boolean) =>
      api.post<FirewallResult>(
        `/olts/${encodeURIComponent(oltId)}/onus/${encodeURIComponent(onuId)}/firewall`,
        { enabled, dryRun: false },
      ),
    onSuccess: async (result, enabled) => {
      const queued = result?.executed === false;
      onNotice(
        queued
          ? `Firewall ${enabled ? 'on' : 'off'} — queued; it applies when the ONU next answers.`
          : (result?.warnings?.[0] ?? `Firewall turned ${enabled ? 'on' : 'off'}.`),
      );
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['device', deviceId] }),
        queryClient.invalidateQueries({ queryKey: ['onu', onuId, 'omci-config'] }),
      ]);
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not change the firewall', 'danger'),
  });

  const canWrite = can('onu.provision');

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted" />
          Security
        </span>
      }
    >
      <div className="divide-y divide-line">
        {/* Firewall — the one control that decides whether the WAN answers. */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3.5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-ink">Firewall</span>
              {firewalled != null && (
                <Pill state tone={firewalled ? 'success' : 'warn'}>{firewalled ? 'on' : 'off'}</Pill>
              )}
            </div>
            <p className="mt-0.5 text-xs text-faint">
              {firewallSupported
                ? 'Whether the ONU answers unsolicited traffic from the WAN side.'
                : vendor === 'huawei'
                  ? 'Not controllable from here — this ONU is on OMCI and its OLT has no firewall verb. Put it on TR-069 to control it.'
                  : 'This model exposes no firewall control.'}
            </p>
          </div>
          {firewallSupported && canWrite && (
            <div className="inline-flex overflow-hidden rounded-lg border border-line">
              {(
                [
                  ['on', true],
                  ['off', false],
                ] as const
              ).map(([label, value]) => {
                const active = firewalled === value;
                return (
                  <button
                    key={label}
                    type="button"
                    disabled={setFirewall.isPending}
                    onClick={() => setFirewall.mutate(value)}
                    className={`px-4 py-1.5 text-sm capitalize transition ${
                      active
                        ? value
                          ? 'bg-success text-white'
                          : 'bg-warn text-white'
                        : 'text-muted hover:bg-surface-2'
                    } ${setFirewall.isPending ? 'cursor-not-allowed opacity-70' : ''}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          )}
          {setFirewall.isPending && <Spinner className="size-4" />}
        </div>

        {/* Port-forwarding — a pure TR-069 feature, shown only where it lives. */}
        {deviceId && device?.portForward.supported ? (
          <PortForwards
            deviceId={deviceId}
            entries={device.portForward.entries}
            canWrite={can('device.write')}
            remoteHostSupported={device.portForward.remoteHostSupported}
            onNotice={onNotice}
          />
        ) : (
          <div className="px-5 py-3.5">
            <span className="text-sm font-medium text-ink">Port forwarding</span>
            <p className="mt-0.5 text-xs text-faint">
              {deviceId
                ? 'This ONU reports no port-forwarding table — the model may not support it, or it has not been read yet.'
                : 'Available once the ONU is on TR-069 — a forward lives on the ONU itself.'}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
}

const PROTOCOLS = ['TCP', 'UDP'] as const;

export function PortForwards({
  deviceId,
  entries,
  canWrite,
  remoteHostSupported,
  onNotice,
}: {
  deviceId: string;
  entries: PortForward[];
  canWrite: boolean;
  remoteHostSupported: boolean;
  onNotice: Notify;
}) {
  const queryClient = useQueryClient();
  // null = form closed; 'add' = adding; a number = editing that entry's index.
  const [formFor, setFormFor] = useState<'add' | number | null>(null);
  const [protocol, setProtocol] = useState<(typeof PROTOCOLS)[number]>('TCP');
  const [externalPort, setExternalPort] = useState('');
  const [internalClient, setInternalClient] = useState('');
  const [internalPort, setInternalPort] = useState('');
  const [description, setDescription] = useState('');
  const [remoteHost, setRemoteHost] = useState('');
  // An open add/edit form is a draft: the Port forwarding panel keeps it
  // mounted while the operator glances at another panel.
  useUnsavedDraft(formFor !== null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['device', deviceId] });

  const close = () => {
    setFormFor(null);
    setProtocol('TCP');
    setExternalPort('');
    setInternalClient('');
    setInternalPort('');
    setDescription('');
    setRemoteHost('');
  };

  const openAdd = () => {
    close();
    setFormFor('add');
  };
  const openEdit = (e: PortForward) => {
    setProtocol((e.protocol?.toUpperCase() as (typeof PROTOCOLS)[number]) ?? 'TCP');
    setExternalPort(e.externalPort != null ? String(e.externalPort) : '');
    setInternalClient(e.internalClient ?? '');
    setInternalPort(e.internalPort != null ? String(e.internalPort) : '');
    setDescription(e.description ?? '');
    setRemoteHost(e.remoteHost ?? '');
    setFormFor(e.index);
  };

  const body = () => ({
    protocol,
    externalPort: Number(externalPort),
    internalPort: Number(internalPort || externalPort),
    internalClient: internalClient.trim(),
    description: description.trim(),
    ...(remoteHostSupported ? { remoteHost: remoteHost.trim() } : {}),
  });

  const add = useMutation({
    mutationFn: () =>
      api.post(`/devices/${encodeURIComponent(deviceId)}/port-forwards`, {
        ...body(),
        description: description.trim() || undefined,
      }),
    onSuccess: async () => {
      onNotice(`Forwarding ${protocol} ${externalPort} → ${internalClient.trim()}.`);
      close();
      await refresh();
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not add the forward', 'danger'),
  });

  const edit = useMutation({
    mutationFn: (index: number) =>
      api.patch(`/devices/${encodeURIComponent(deviceId)}/port-forwards/${index}`, body()),
    onSuccess: async () => {
      onNotice('Forward updated.');
      close();
      await refresh();
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not update the forward', 'danger'),
  });

  const toggle = useMutation({
    mutationFn: (e: PortForward) =>
      api.patch(`/devices/${encodeURIComponent(deviceId)}/port-forwards/${e.index}`, {
        enabled: e.enabled === false,
      }),
    onSuccess: async (_r, e) => {
      onNotice(`Forward ${e.enabled === false ? 'enabled' : 'disabled'}.`);
      await refresh();
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not change the forward', 'danger'),
  });

  const remove = useMutation({
    mutationFn: (index: number) =>
      api.delete(`/devices/${encodeURIComponent(deviceId)}/port-forwards/${index}`),
    onSuccess: async () => {
      onNotice('Forward removed.');
      await refresh();
    },
    onError: (err) => onNotice(err instanceof Error ? err.message : 'Could not remove the forward', 'danger'),
  });

  const IPV4 = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;
  const ready =
    Number(externalPort) >= 1 &&
    Number(externalPort) <= 65535 &&
    IPV4.test(internalClient.trim()) &&
    (internalPort === '' || (Number(internalPort) >= 1 && Number(internalPort) <= 65535)) &&
    (remoteHost.trim() === '' || IPV4.test(remoteHost.trim()));
  const busy = add.isPending || edit.isPending;

  return (
    <div className="px-5 py-3.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-ink">Port forwarding</span>
        {canWrite && formFor === null && <Button onClick={openAdd}>Add a forward</Button>}
      </div>

      {entries.length === 0 ? (
        <p className="mt-1 text-xs text-faint">No forwards — the ONU answers nothing inbound on its own.</p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {entries.map((e) => (
            <li
              key={e.index}
              className="flex items-center justify-between gap-3 rounded-lg border border-line bg-surface-2 px-3 py-2"
            >
              <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
                <Pill tone="neutral">{e.protocol ?? '—'}</Pill>
                <span className="font-mono text-ink">{e.externalPort ?? '—'}</span>
                <ArrowRight className="size-3.5 text-faint" />
                <span className="font-mono text-ink">
                  {e.internalClient ?? '—'}
                  {e.internalPort != null ? `:${e.internalPort}` : ''}
                </span>
                {e.remoteHost && e.remoteHost !== '0.0.0.0' && (
                  <span className="text-xs text-faint">from {e.remoteHost}</span>
                )}
                {e.description && <span className="text-xs text-faint">{e.description}</span>}
                {e.enabled === false && <Pill tone="warn">disabled</Pill>}
              </div>
              {canWrite && (
                <div className="flex shrink-0 items-center gap-2.5">
                  <button
                    type="button"
                    title={e.enabled === false ? 'Enable this forward' : 'Disable this forward'}
                    disabled={toggle.isPending}
                    onClick={() => toggle.mutate(e)}
                    className="text-xs text-link hover:underline"
                  >
                    {e.enabled === false ? 'Enable' : 'Disable'}
                  </button>
                  <button
                    type="button"
                    title="Edit this forward"
                    onClick={() => openEdit(e)}
                    className="text-faint hover:text-ink"
                  >
                    <Pencil className="size-4" />
                  </button>
                  <button
                    type="button"
                    title="Remove this forward"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(e.index)}
                    className="text-faint hover:text-danger"
                  >
                    <Trash2 className="size-4" />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {formFor !== null && (
        <div className="mt-3 rounded-lg border border-line p-3">
          <div className="grid gap-2 sm:grid-cols-[5rem_1fr_1fr]">
            <label className="block">
              <span className="label">Protocol</span>
              <select
                className="input"
                value={protocol}
                onChange={(e) => setProtocol(e.target.value as (typeof PROTOCOLS)[number])}
              >
                {PROTOCOLS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="label">External port</span>
              <Input
                value={externalPort}
                onChange={(e) => setExternalPort(e.target.value.replace(/[^\d]/g, ''))}
                placeholder="8080"
                inputMode="numeric"
              />
            </label>
            <label className="block">
              <span className="label">Description — optional</span>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="CCTV"
              />
            </label>
            <label className="block">
              <span className="label">To LAN client</span>
              <Input
                value={internalClient}
                onChange={(e) => setInternalClient(e.target.value)}
                placeholder="192.168.1.10"
              />
            </label>
            <label className="block">
              <span className="label">To port — defaults to external</span>
              <Input
                value={internalPort}
                onChange={(e) => setInternalPort(e.target.value.replace(/[^\d]/g, ''))}
                placeholder={externalPort || '80'}
                inputMode="numeric"
              />
            </label>
            {remoteHostSupported && (
              <label className="block">
                <span className="label">From source — optional, blank = any</span>
                <Input
                  value={remoteHost}
                  onChange={(e) => setRemoteHost(e.target.value)}
                  placeholder="any WAN source"
                />
              </label>
            )}
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button onClick={close}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!ready || busy}
              onClick={() => (formFor === 'add' ? add.mutate() : edit.mutate(formFor))}
            >
              {busy ? 'Saving…' : formFor === 'add' ? 'Add forward' : 'Save changes'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
